import type { BeforeSendFn, CaptureResult, Properties } from 'posthog-js';

import {
  cortarFragmentosDeUrl,
  redactSensitiveQueryParams,
} from '@/shared/utils/datadogRumRedaction';

/**
 * Redação de credencial em URL coletada pelo PostHog (LGPD).
 *
 * O SDK anexa a URL da página (`$current_url`, `$pathname`, `$referrer`, `$initial_*` em
 * `$set_once`, `href` no `$elements_chain` do autocapture, chave do `$heatmap_data`) a TODO
 * evento. Várias rotas carregam credencial no caminho — o magic link do portal, o convite do
 * advogado —, e é exatamente nelas que os eventos de acesso e cadastro disparam. Quem lê o
 * PostHog não pode sair com um link que ainda autentica.
 *
 * Query string reaproveita a redação do Datadog (mesma denylist: `token`, `code`, `portalHash`,
 * `hash`…). O caminho é tratado aqui porque o Datadog ainda não o redige (issue web#1084); quando
 * aquela issue for feita, `redactCredentialPath` é o ponto de reuso.
 *
 * Fora de alcance: `$snapshot_data` do session replay. Percorrer o DOM serializado a cada envio
 * custaria caro no browser; a URL da gravação segue sendo dívida do replay, não destes eventos.
 */

const REDACTED_PATH_SEGMENT = 'REDACTED';

/** Rotas de `src/app` cujo segmento seguinte é token de uso direto. */
const CREDENTIAL_PATH_PATTERN =
  /(\/(?:portal\/magic|auth\/token|auth\/verify-email|convite-advogado|convite-compartilhado|convite))\/[^/?#\s"']+/g;

/**
 * `/portal/[hash]` é rota dinâmica de um segmento só: tudo que não for rota estática do portal é
 * o hash de acesso do notificado. A lista precisa acompanhar `src/app/portal` — rota estática nova
 * que fique fora daqui só perde o nome no PostHog, não vaza nada.
 */
const PORTAL_HASH_PATTERN =
  /(\/portal)\/(?!(?:magic|sessao-expirada|arbitragem|dashboard|duvidas|notificacao)(?:[/?#\s"']|$))[^/?#\s"']+/g;

/** Chave cujo conteúdo é o DOM serializado do replay — ver nota no topo. */
const SESSION_REPLAY_DATA_KEY = '$snapshot_data';

export function redactCredentialPath(value: string): string {
  return value
    .replace(CREDENTIAL_PATH_PATTERN, `$1/${REDACTED_PATH_SEGMENT}`)
    .replace(PORTAL_HASH_PATTERN, `$1/${REDACTED_PATH_SEGMENT}`);
}

/** O fragmento sai antes: o token do link da tela de envio da Arbitragem mora nele (20q D9). */
function redactCredentialsInText(value: string): string {
  return redactSensitiveQueryParams(redactCredentialPath(cortarFragmentosDeUrl(value)));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype
  );
}

function redactValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return redactCredentialsInText(value);
  }

  if (Array.isArray(value)) {
    return value.map(redactValue);
  }

  if (isPlainObject(value)) {
    return redactProperties(value);
  }

  return value;
}

/** Redige chaves também: `$heatmap_data` é indexado pela URL da página. */
function redactProperties(properties: Properties): Properties {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [
      redactCredentialsInText(key),
      key === SESSION_REPLAY_DATA_KEY ? value : redactValue(value),
    ]),
  );
}

/**
 * `before_send` do PostHog. Falha FECHADA: se a redação lançar, o evento é descartado (`null`) —
 * publicar pela metade é justamente o vazamento que este controle existe para impedir.
 */
export const redactPostHogEvent: BeforeSendFn = (captured: CaptureResult | null) => {
  if (!captured) {
    return null;
  }

  try {
    return {
      ...captured,
      properties: redactProperties(captured.properties),
      ...(captured.$set && { $set: redactProperties(captured.$set) }),
      ...(captured.$set_once && { $set_once: redactProperties(captured.$set_once) }),
    };
  } catch {
    return null;
  }
};
